import React, { useMemo, useState } from 'react';
import {
  addMonths,
  endOfMonth,
  format,
  getDay,
  isBefore,
  isSameDay,
  parseISO,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Calendar grid date picker.
 *   availableDates = ["2026-05-25", "2026-05-26", ...]
 *   selectedDate   = "2026-05-25" | null
 *   onSelect(dateStr)
 */
export function DatePicker({ availableDates, selectedDate, onSelect }) {
  const today = startOfDay(new Date());
  const earliestAvailable = useMemo(() => {
    if (!availableDates?.length) return today;
    return availableDates.map((d) => parseISO(d)).sort((a, b) => a - b)[0];
  }, [availableDates, today]);

  // Show the month containing the first available date (or current month).
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(earliestAvailable));

  const availableSet = useMemo(() => new Set(availableDates || []), [availableDates]);

  const monthStart = startOfMonth(viewMonth);
  const monthEnd = endOfMonth(viewMonth);
  const leadingBlanks = getDay(monthStart); // 0 = Sun
  const daysInMonth = monthEnd.getDate();

  const cells = [];
  for (let i = 0; i < leadingBlanks; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(new Date(viewMonth.getFullYear(), viewMonth.getMonth(), d));
  }

  return (
    <div className="space-y-3 animate-fade-in">
      {/* Month nav */}
      <div className="flex items-center justify-between">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => setViewMonth((m) => addMonths(m, -1))}
          disabled={isBefore(startOfMonth(addMonths(viewMonth, -1)), startOfMonth(today))}
          aria-label="Previous month"
          className="h-8 w-8"
        >
          <ChevronLeft className="size-4" />
        </Button>
        <div className="text-sm font-semibold tracking-tight">
          {format(viewMonth, 'MMMM yyyy')}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => setViewMonth((m) => addMonths(m, 1))}
          aria-label="Next month"
          className="h-8 w-8"
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>

      {/* Day labels */}
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>

      {/* Date grid */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={`blank-${i}`} />;
          const iso = format(d, 'yyyy-MM-dd');
          const isAvailable = availableSet.has(iso);
          const isPast = isBefore(d, today);
          const isSelected = selectedDate === iso;
          const isToday = isSameDay(d, today);

          return (
            <button
              key={iso}
              type="button"
              disabled={!isAvailable || isPast}
              onClick={() => onSelect(iso)}
              className={cn(
                'relative h-10 rounded-md text-sm font-medium transition-all',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                isSelected && 'bg-primary text-primary-foreground shadow-soft',
                !isSelected && isAvailable && !isPast && 'hover:bg-primary-soft hover:text-primary',
                !isAvailable && !isPast && 'text-muted-foreground/40 cursor-not-allowed',
                isPast && 'text-muted-foreground/30 cursor-not-allowed',
              )}
            >
              {format(d, 'd')}
              {isToday && !isSelected && (
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 size-1 rounded-full bg-primary" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
